import { ManifestResolver } from './ManifestResolver';
import { ManifestQuery } from './ManifestQuery';
import { IManifestChangeDetection } from '../ManifestChangeDetection';
import { ManifestServiceFlags } from '../ManifestServiceFlags';
import { CacheMethodResponse } from './decorators';
import { MethodCacheStrategy } from './MethodCacheStrategy';
import {
  VirtualElement,
  VirtualElementAPIType,
  DependenciesProvider,
} from './providers';
import { ManifestServiceStats } from '../ManifestServiceStats';
import { IVirtualManifest } from '../builders';
import { EditorPointerServiceAPI } from '@wix/editor-document-logic-api';

export class VirtualManifestAPI {
  readonly resolver: ManifestResolver;
  readonly query: ManifestQuery;
  readonly virtualManifest: IVirtualManifest;

  constructor(
    public readonly node: VirtualElement<VirtualElementAPIType.VirtualManifestAPI>,
    private readonly dependenciesProvider: DependenciesProvider,
    private readonly editorPointerAPI: EditorPointerServiceAPI,
    private readonly manifestChangeDetection: IManifestChangeDetection,
    private readonly manifestServiceFlags: ManifestServiceFlags,
    private readonly manifestServiceStats: ManifestServiceStats,
    private readonly isReadonly: boolean = false,
  ) {
    const virtualManifest = node.getVirtualManifest();
    this.virtualManifest = virtualManifest;

    const _dependenciesProvider = this.dependenciesProvider.withOverrides(
      virtualManifest.overriddenDependencies,
    );

    const manifestQueryAPI = _dependenciesProvider.getManifestQueryAPI();
    const manifestResolverAPI = _dependenciesProvider.getManifestResolverAPI();

    this.resolver = new ManifestResolver(
      this.node.lockOnResolverAPI(),
      manifestResolverAPI,
      this.editorPointerAPI,
      this.manifestChangeDetection,
      this.manifestServiceFlags,
      this.manifestServiceStats,
    );

    this.query = new ManifestQuery(
      this.node.lockOnQueryAPI(),
      manifestQueryAPI,
      this.editorPointerAPI,
      this.manifestChangeDetection,
      this.manifestServiceFlags,
      this.manifestServiceStats,
      _dependenciesProvider,
      this.isReadonly,
      this.node.getOperationalContext(),
    );
  }

  getEditorPointer() {
    return this.node.getEditorPointer();
  }

  getExtensionType() {
    return this.node.getExtensionType();
  }

  isMultipleComponentsRoot() {
    return this.node.isMultipleComponentsRoot();
  }

  getDisplayName() {
    return this.resolver.getDisplayName();
  }

  @CacheMethodResponse({
    cacheStrategy: MethodCacheStrategy.Permanent,
    transformResponseForStatsCollecting: (x: VirtualManifestAPI[]) =>
      x.map((child) => child.getOwnProperties()),
  })
  getChildren(options?: { recursive?: boolean }) {
    return this.node.getChildren(options).map((child) => {
      return child.getAPI();
    });
  }

  @CacheMethodResponse({
    cacheStrategy: MethodCacheStrategy.Permanent,
    transformResponseForStatsCollecting: (x: VirtualManifestAPI) =>
      x?.getOwnProperties(),
  })
  getChild(path: string[]) {
    return this.node.getChild(path)?.getAPI();
  }

  /**
   * for platform stat collector purposes only
   */
  getOwnProperties() {
    return {
      resolvedVirtualManifest: this.node.getVirtualManifest(),
    };
  }
}
